import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "@/common/components/ui/button";
import { Alert, AlertDescription } from "@/common/components/ui/alert";
import { AlertCircle, BarChart3, FileUp, Filter } from "lucide-react";
import { useQuery } from "@apollo/client/react";
import {
  GetLedgerOverviewDocument,
  GetLedgerOverviewValuationDocument,
} from "@/graphql/definitions";
import { Link, useLoaderData, useParams } from "@tanstack/react-router";
import { useLedgerSearchParams } from "@/common/hooks/use-ledger-search-params";
import { createLedgerId } from "@/common/lib/utils/encode";
import { useLedger } from "@/common/hooks/use-ledger";
import { RelatedLinks } from "@/common/components/related-links";
import { ErrorBoundary } from "@/common/components/error-boundary";
import { useTranslations } from "@/common/hooks/use-translations";
import { getErrorMessageKey } from "@/common/lib/errors/error-message";
import CashFlowSankey from "./components/cash-flow-sankey";
import { overviewQueryDefaults } from "./constants";
import { ReadmeCard } from "@/common/components/readme-card";
import { useLedgerReadme } from "@/common/hooks/use-ledger-readme";
import type { InitialLedgerReadme } from "@/common/lib/ledger-readme";
import { resolveLedgerPresentation } from "@/common/lib/seo/ledger-presentation";
import { getInvertIncomeLiabilitiesEquity } from "@/common/lib/fava-options";
import { StarButton } from "./components/star-button";
import { useLedgerPermission } from "@/common/hooks/use-ledger-permission";
import { QuickAskInput } from "@/features/ai-agent/components/quick-ask-input";
import { IncomeExpensesChart } from "./components/income-expenses-chart";
import { DistributionChart } from "./components/distribution-chart";
import { LedgerWritePermission } from "@/common/components/ledger-permission/write";
import { NetWorthCard } from "./components/net-worth-card";
import { AccountBalancesCard } from "./components/account-balances-card";
import { MoneyMovementSection } from "./components/money-movement-section";
import { RecentActivityCard } from "./components/recent-activity-card";
import {
  CustomizeButton,
  DashboardCustomizer,
} from "./components/dashboard-customizer";
import {
  type DashboardWidgetId,
  useDashboardLayout,
} from "./hooks/use-dashboard-layout";
import { useAccountMeta } from "./hooks/use-account-meta";
import { ReportEmptyState } from "@/common/components/state-components";
import { hasOverviewActivity, toLocalISODate } from "./lib/overview-utils";
import { describeLatestNetWorth } from "./lib/net-worth-valuation";
import { EmptyLedgerSetup } from "./components/empty-ledger-setup";

function PublicLedgerIntroduction({
  ledgerId,
  initialReadme,
  name,
  description,
  readmeVisible,
}: {
  ledgerId: string;
  initialReadme?: InitialLedgerReadme;
  name: string;
  description?: string | null;
  readmeVisible: boolean;
}) {
  const { t } = useTranslations();
  const { content } = useLedgerReadme(ledgerId, initialReadme);
  const presentation = resolveLedgerPresentation({
    name,
    description,
    readme: content,
    fallbackDescription: t("common.pageDescription.overview", {
      ledgerName: name,
    }),
  });
  return (
    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
      {presentation.description}
      {readmeVisible && content && (
        <>
          {" "}
          <a
            href="#overview-ledger-notes"
            className="whitespace-nowrap text-foreground underline underline-offset-4"
          >
            {t("page.overview.ledgerNotes")}
          </a>
        </>
      )}
    </p>
  );
}

/**
 * Overview page component
 * This page shows ledger overview information
 */
export default function LedgerOverviewPage() {
  const { t } = useTranslations();
  const { ledgerOwner, ledgerName } = useParams({
    from: "/ledger/$ledgerOwner/$ledgerName/",
  });
  const loaderData = useLoaderData({
    from: "/ledger/$ledgerOwner/$ledgerName/",
  });
  const initialReadme = loaderData?.readme;

  const ledgerId = createLedgerId(ledgerOwner, ledgerName);
  const {
    primaryCurrency,
    ledgerName: ledgerDisplayName,
    ledgerData,
  } = useLedger();
  const { isAdmin, canWrite } = useLedgerPermission();
  const publicReader = !ledgerData.private && !canWrite;
  const { layout, setVisible, move, reset } = useDashboardLayout(ledgerId);
  // One customization panel for both entry points; see DashboardCustomizer.
  const [customizing, setCustomizing] = useState(false);
  const customizeOpener = useRef<HTMLElement | null>(null);
  const overviewHeading = useRef<HTMLHeadingElement>(null);
  const ledgerFilters = useLedgerSearchParams();
  const invertIncomeLiabilitiesEquity =
    getInvertIncomeLiabilitiesEquity(ledgerData);

  // Flows (money movement, income vs expenses, cash flow) at cost.
  const {
    data,
    loading: overviewLoading,
    error,
  } = useQuery(GetLedgerOverviewDocument, {
    variables: {
      ledgerId: ledgerId,
      account: ledgerFilters.searchParams.account,
      filter: ledgerFilters.searchParams.filter,
      time: ledgerFilters.searchParams.time,
      ...overviewQueryDefaults,
    },
    fetchPolicy: "cache-first",
  });
  // Balances (net worth, account balances, distribution) at market value.
  const {
    data: valuationData,
    loading: valuationLoading,
    error: valuationError,
  } = useQuery(GetLedgerOverviewValuationDocument, {
    variables: {
      ledgerId: ledgerId,
      account: ledgerFilters.searchParams.account,
      filter: ledgerFilters.searchParams.filter,
      time: ledgerFilters.searchParams.time,
      interval: overviewQueryDefaults.interval,
    },
    fetchPolicy: "cache-first",
  });

  const { accountMeta, pending: accountMetaPending } = useAccountMeta(ledgerId);

  const today = toLocalISODate(new Date());
  const market = valuationData?.market;
  const costNetWorth = data?.getLedgerOverview?.netWorthData;
  const netWorthValuation = useMemo(
    () =>
      describeLatestNetWorth({
        market: market?.netWorthData ?? [],
        cost: costNetWorth ?? [],
        units: valuationData?.held?.netWorthData ?? [],
        currency: primaryCurrency,
        today,
        pricePairs: valuationData?.getLedgerCommodities ?? [],
        managedSources: valuationData?.getLedgerManagedPrices ?? [],
      }),
    [market, costNetWorth, valuationData, primaryCurrency, today],
  );

  // Do not keep a prior period's overview cards while a replacement read is in
  // flight — the URL/filters already name the new scope. Both reads gate the
  // page, so a balance is never drawn at cost and then redrawn at market.
  let reportState: ReactNode;
  if (overviewLoading || valuationLoading) {
    reportState = (
      <div className="space-y-6 md:space-y-8">
        <Card>
          <CardContent>
            <div
              className="flex items-center justify-center py-14"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="text-center space-y-3">
                <div className="mx-auto h-8 w-8 rounded-full border-b-2 border-primary animate-spin" />
                <p className="text-sm text-muted-foreground">
                  {t("page.overview.loading")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  } else if (error) {
    reportState = (
      <div className="space-y-6 md:space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 tracking-tight">
              <AlertCircle className="h-5 w-5" />
              {t("component.errorState.title")}
            </CardTitle>
            <CardDescription>{t("page.overview.failedToLoad")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                {t(getErrorMessageKey(error))}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Public explanations remain usable while reports fail or have no matching
  // activity. Writers retain the existing report loading/error experience.
  if (reportState && !publicReader) return reportState;

  const showStarButton = !isAdmin;
  const isStarred = ledgerData?.isStarred ?? false;
  const displayName = publicReader
    ? resolveLedgerPresentation({
        name: ledgerData.name ?? ledgerDisplayName ?? ledgerName,
        title: ledgerData.options.title,
        fallbackDescription: "",
      }).title
    : (ledgerDisplayName ?? ledgerName);
  const overview = data?.getLedgerOverview;
  const hasActivity = hasOverviewActivity(overview);
  const hasActiveFilters = Boolean(
    ledgerFilters.searchParams.account ||
    ledgerFilters.searchParams.filter ||
    ledgerFilters.searchParams.time,
  );
  // The flows still load without the market read; the balance modules say
  // they could not, rather than fall back to a figure at cost.
  const balancesError = valuationError ? (
    <Alert variant="destructive">
      <AlertDescription>
        {t(getErrorMessageKey(valuationError))}
      </AlertDescription>
    </Alert>
  ) : null;
  const widgets: Record<DashboardWidgetId, ReactNode> = {
    "financial-position": (
      <section
        aria-labelledby="overview-financial-position-heading"
        className="space-y-3"
      >
        <div>
          <h2
            id="overview-financial-position-heading"
            className="text-lg font-semibold tracking-tight"
          >
            {t("page.overview.financialPosition")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("page.overview.financialPositionDescription")}
          </p>
        </div>
        {balancesError ?? (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(19rem,0.8fr)]">
            <NetWorthCard
              data={market?.netWorthData ?? []}
              valuation={netWorthValuation}
              primaryCurrency={primaryCurrency}
              ledgerOwner={ledgerOwner}
              ledgerName={ledgerName}
              today={today}
            />
            <AccountBalancesCard
              assets={market?.assetsHierarchyData}
              liabilities={market?.liabilitiesHierarchyData}
              primaryCurrency={primaryCurrency}
              invertLiabilities={invertIncomeLiabilitiesEquity}
              ledgerOwner={ledgerOwner}
              ledgerName={ledgerName}
              time={ledgerFilters.searchParams.time}
              fiscalYearEnd={ledgerData.favaOptions?.fiscalYearEnd}
            />
          </div>
        )}
      </section>
    ),
    "money-movement": (
      <MoneyMovementSection
        income={overview?.incomeIntervalData ?? []}
        expenses={overview?.expensesIntervalData ?? []}
        primaryCurrency={primaryCurrency}
        ledgerOwner={ledgerOwner}
        ledgerName={ledgerName}
        preferActiveMonth={publicReader}
      />
    ),
    "recent-activity": (
      <RecentActivityCard
        ledgerId={ledgerId}
        ledgerOwner={ledgerOwner}
        ledgerName={ledgerName}
        account={ledgerFilters.searchParams.account}
        filter={ledgerFilters.searchParams.filter}
        time={ledgerFilters.searchParams.time}
        primaryCurrency={primaryCurrency}
        incomeRoot={ledgerData.options.nameIncome}
        expensesRoot={ledgerData.options.nameExpenses}
        canWrite={canWrite}
      />
    ),
    "income-expenses": (
      <section
        aria-labelledby="overview-income-expenses-heading"
        className="space-y-3"
      >
        <h2
          id="overview-income-expenses-heading"
          className="text-lg font-semibold tracking-tight"
        >
          {t("page.reports.incomeVsExpenses")}
        </h2>
        <Card className="min-w-0 gap-0 overflow-hidden py-0">
          <CardHeader className="border-b py-5">
            <CardTitle>{t("page.reports.incomeVsExpenses")}</CardTitle>
            <CardDescription>
              {t("page.reports.incomeVsExpensesDescription")}
            </CardDescription>
            <CardAction>
              <span className="flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <BarChart3 className="size-4" />
              </span>
            </CardAction>
          </CardHeader>
          <CardContent className="px-3 py-4 sm:px-6 sm:py-5">
            <IncomeExpensesChart
              income={overview?.incomeIntervalData ?? []}
              expenses={overview?.expensesIntervalData ?? []}
              primaryCurrency={primaryCurrency}
            />
          </CardContent>
        </Card>
      </section>
    ),
    "balance-sheet": (
      <section
        aria-labelledby="overview-position-heading"
        className="space-y-3"
      >
        <h2
          id="overview-position-heading"
          className="text-lg font-semibold tracking-tight"
        >
          {t("common.balanceSheet")}
        </h2>
        {balancesError ?? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DistributionChart
              title={t("page.overview.assetsDistribution")}
              description={t("page.overview.assetsDistributionDescription", {
                ledgerName: displayName,
              })}
              data={market?.assetsHierarchyData}
              primaryCurrency={primaryCurrency}
            />
            <DistributionChart
              title={t("page.overview.liabilitiesDistribution")}
              description={t(
                "page.overview.liabilitiesDistributionDescription",
                {
                  ledgerName: displayName,
                },
              )}
              data={market?.liabilitiesHierarchyData}
              primaryCurrency={primaryCurrency}
              inverse
            />
          </div>
        )}
      </section>
    ),
    "cash-flow": (
      <section
        aria-labelledby="overview-cash-flow-heading"
        className="space-y-3"
      >
        <h2
          id="overview-cash-flow-heading"
          className="text-lg font-semibold tracking-tight"
        >
          {t("page.overview.cashFlow")}
        </h2>
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>{t("page.overview.cashFlow")}</CardTitle>
            <CardDescription>
              {t("page.overview.cashFlowDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CashFlowSankey
              incomeHierarchyData={overview?.incomeHierarchyData}
              expensesHierarchyData={overview?.expensesHierarchyData}
              assetsHierarchyData={overview?.assetsHierarchyData}
              liabilitiesHierarchyData={overview?.liabilitiesHierarchyData}
              accountMeta={accountMeta}
              primaryCurrency={primaryCurrency}
              accountMetaPending={accountMetaPending}
            />
          </CardContent>
        </Card>
      </section>
    ),
    readme: (
      <div id="overview-ledger-notes" className="scroll-mt-4">
        <ReadmeCard
          ledgerId={ledgerId}
          initialReadme={initialReadme}
          headingOffset={1}
        />
      </div>
    ),
  };

  const visibleWidgetIds = layout.order.filter(
    (id) => !layout.hidden.includes(id),
  );
  const narrativeOnly = Boolean(reportState) || !hasActivity;

  return (
    <div className="space-y-6 pb-4 md:space-y-8">
      <section className="relative overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="pointer-events-none absolute -top-32 -left-24 size-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -top-24 right-0 size-64 rounded-full bg-chart-4/10 blur-3xl" />
        <div className="relative p-4 sm:px-5 sm:py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-medium tracking-[0.16em] text-muted-foreground uppercase">
                {t("common.overview")}
              </p>
              <h1
                ref={overviewHeading}
                tabIndex={-1}
                className={`mt-1 text-xl font-semibold tracking-tight outline-none sm:text-2xl ${publicReader ? "" : "truncate"}`}
              >
                {displayName}
              </h1>
              {publicReader ? (
                <PublicLedgerIntroduction
                  ledgerId={ledgerId}
                  initialReadme={initialReadme}
                  name={displayName}
                  description={ledgerData.description}
                  readmeVisible={visibleWidgetIds.includes("readme")}
                />
              ) : (
                <p className="mt-1 max-w-2xl text-xs leading-snug text-muted-foreground sm:text-sm">
                  {t("common.pageDescription.overview", {
                    ledgerName: displayName,
                  })}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <LedgerWritePermission>
                <Button
                  asChild
                  size="sm"
                  variant="secondary"
                  className="rounded-full px-3"
                >
                  <Link
                    to="/ledger/$ledgerOwner/$ledgerName/import"
                    params={{ ledgerOwner, ledgerName }}
                  >
                    <FileUp />
                    {t("common.relatedLinks.import")}
                  </Link>
                </Button>
              </LedgerWritePermission>
              {showStarButton && (
                <StarButton
                  ledgerId={ledgerId}
                  isStarred={isStarred}
                  starLabel={t("page.overview.starButton.star")}
                  starredLabel={t("page.overview.starButton.starred")}
                />
              )}
              <DashboardCustomizer
                showTrigger={!publicReader}
                layout={layout}
                setVisible={setVisible}
                move={move}
                reset={reset}
                open={customizing}
                onOpenChange={(open) => {
                  // Opened by its own header trigger: Radix returns focus there.
                  if (open) customizeOpener.current = null;
                  setCustomizing(open);
                }}
                returnFocus={customizeOpener}
                closeFocusFallback={overviewHeading}
              />
            </div>
          </div>

          <div className="mt-3 max-w-3xl">
            <QuickAskInput />
          </div>
        </div>
      </section>

      {reportState ? (
        <div id="overview-report-state">{reportState}</div>
      ) : !hasActivity ? (
        <div id="overview-report-state">
          {hasActiveFilters ? (
            <ReportEmptyState
              Icon={Filter}
              title={t("page.overview.filteredEmptyTitle")}
              message={t("page.overview.filteredEmptyDescription")}
            />
          ) : (
            <EmptyLedgerSetup
              ledgerOwner={ledgerOwner}
              ledgerName={ledgerName}
              entryFile={
                ledgerData.bcioOptions.transactionFile ??
                ledgerData.bcioOptions.defaultFile
              }
              canWrite={canWrite}
            />
          )}
        </div>
      ) : visibleWidgetIds.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {t("page.overview.allWidgetsHidden")}
            </p>
            <CustomizeButton
              aria-haspopup="dialog"
              onClick={(event) => {
                customizeOpener.current = event.currentTarget;
                setCustomizing(true);
              }}
            />
          </CardContent>
        </Card>
      ) : (
        visibleWidgetIds.map((id) => (
          <div key={id} id={`overview-widget-${id}`} className="scroll-mt-4">
            <ErrorBoundary>{widgets[id]}</ErrorBoundary>
          </div>
        ))
      )}

      {publicReader && narrativeOnly && visibleWidgetIds.includes("readme") && (
        <ErrorBoundary>{widgets.readme}</ErrorBoundary>
      )}

      <RelatedLinks
        links={[
          {
            label: t("common.relatedLinks.journal"),
            to: `/ledger/${ledgerOwner}/${ledgerName}/journal`,
          },
          {
            label: t("common.relatedLinks.balanceSheet"),
            to: `/ledger/${ledgerOwner}/${ledgerName}/balance-sheet`,
          },
          {
            label: t("common.relatedLinks.incomeStatement"),
            to: `/ledger/${ledgerOwner}/${ledgerName}/income-statement`,
          },
          {
            label: t("common.relatedLinks.cashFlow"),
            to: `/ledger/${ledgerOwner}/${ledgerName}/cash-flow`,
          },
        ]}
      />
    </div>
  );
}
